import React, { useState } from "react";
import { Button, InputNumber, Popconfirm, Select, Switch, Table, Tag, notification } from "antd";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import styles from "./style.module.scss";

// Technical view of sentiment active learning, for Pivony experts only (the API answers 403 to anyone else).
// Settings per source, Opus spend, the flow's counts, label quality checks, golden-set coverage and model versions.

const CLASSES = [
  ["1.0", "olumlu"],
  ["0.0", "olumsuz"],
  ["0.5", "nötr"],
  ["33", "çatışma"],
];
const NUMBER_FIELDS = [
  ["threshold", 0, 1, 0.01],
  ["daily_opus_pairs", 0, 5000, 10],
  ["monthly_budget_usd", 0, 10000, 5],
  ["pool_size", 0, 100000, 100],
  ["spot_check_rate", 0, 1, 0.01],
  ["lowest_share", 0, 1, 0.05],
  ["daily_queue_limit", 0, 1000, 5],
];

const fmt = (v, digits = 3) => (v == null ? "–" : Number(v).toFixed(digits));
const usd = (v) => (v == null ? "–" : `$${Number(v).toFixed(2)}`);

function ClassTable({ block }) {
  if (!block) return null;
  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th />
          {CLASSES.map(([, name]) => (
            <th key={name}>{name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {["precision", "recall", "f1", "support"].map((metric) => (
          <tr key={metric}>
            <td>{metric}</td>
            {CLASSES.map(([value]) => (
              <td key={value}>
                {metric === "support" ? block.per_class?.[value]?.support : fmt(block.per_class?.[value]?.[metric])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const AdminView = ({ overview, onChanged }) => {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState({});

  const draft = (source, field, value) =>
    setDrafts((d) => ({ ...d, [source]: { ...(d[source] || {}), [field]: value } }));

  const save = async (source) => {
    const res = await apiRequest(`active-learning/admin/config/${source}`, "POST", drafts[source] || {}, true);
    if (!res || res.error) {
      notification.error({ message: res?.error || t("teach_ai.admin.save_failed") });
      return;
    }
    setDrafts((d) => ({ ...d, [source]: undefined }));
    onChanged();
  };

  const decide = async (version, decision) => {
    const res = await apiRequest(`active-learning/admin/versions/${version}/decide`, "POST", { decision }, true);
    if (!res || res.error) {
      notification.error({ message: res?.error || t("teach_ai.admin.save_failed") });
      return;
    }
    onChanged();
  };

  const configColumns = [
    { title: "source", dataIndex: "source", key: "source" },
    { title: "dashboard", dataIndex: "dashboard_id", key: "dashboard_id" },
    {
      title: "enabled",
      key: "enabled",
      render: (row) => (
        <Switch
          size="small"
          checked={drafts[row.source]?.enabled ?? row.enabled}
          onChange={(v) => draft(row.source, "enabled", v)}
        />
      ),
    },
    {
      title: "audience",
      key: "audience",
      render: (row) => (
        <Select
          size="small"
          value={drafts[row.source]?.audience ?? row.audience ?? "power_users"}
          onChange={(v) => draft(row.source, "audience", v)}
          options={[
            { value: "power_users", label: "power users" },
            { value: "everyone", label: "everyone" },
          ]}
        />
      ),
    },
    ...NUMBER_FIELDS.map(([field, min, max, step]) => ({
      title: field,
      key: field,
      render: (row) => (
        <InputNumber
          size="small"
          min={min}
          max={max}
          step={step}
          value={drafts[row.source]?.[field] ?? row[field]}
          onChange={(v) => draft(row.source, field, v)}
          className={styles.numberInput}
        />
      ),
    })),
    {
      key: "save",
      render: (row) =>
        drafts[row.source] ? (
          <Button size="small" type="primary" onClick={() => save(row.source)}>
            {t("teach_ai.admin.save")}
          </Button>
        ) : null,
    },
  ];

  const versionColumns = [
    { title: "version", dataIndex: "version", key: "version" },
    { title: "date", key: "date", render: (v) => v.created_at?.slice(0, 16).replace("T", " ") },
    { title: "train / added / golden", key: "sizes", render: (v) => `${v.train_pairs ?? "–"} / ${v.added_pairs ?? "–"} / ${v.golden_pairs ?? "–"}` },
    { title: "macro F1 new", key: "new", render: (v) => fmt(v.scores?.new?.macro_f1) },
    { title: "macro F1 current", key: "current", render: (v) => fmt(v.scores?.current?.macro_f1) },
    { title: "accuracy", key: "acc", render: (v) => fmt(v.scores?.new?.accuracy) },
    {
      title: "gate",
      key: "gate",
      render: (v) => (v.gate?.passed ? <Tag color="green">passed</Tag> : <Tag color="red">failed</Tag>),
    },
    { title: "status", dataIndex: "status", key: "status" },
    {
      key: "decide",
      render: (v) =>
        v.status === "pending_approval" ? (
          <span className={styles.decide}>
            <Popconfirm title={t("teach_ai.admin.approve_confirm")} onConfirm={() => decide(v.version, "approve")}>
              <Button size="small" type="primary" disabled={!v.gate?.passed}>
                {t("teach_ai.admin.approve")}
              </Button>
            </Popconfirm>
            <Button size="small" onClick={() => decide(v.version, "reject")}>
              {t("teach_ai.admin.reject")}
            </Button>
          </span>
        ) : null,
    },
  ];

  const spot = overview.spot_check || {};
  return (
    <div className={styles.admin}>
      <section>
        <h3>{t("teach_ai.admin.settings")}</h3>
        <Table size="small" rowKey="source" columns={configColumns} dataSource={overview.config} pagination={false} scroll={{ x: true }} />
      </section>

      <section className={styles.adminGrid}>
        <div className={styles.card}>
          <h3>{t("teach_ai.admin.spend")}</h3>
          <div className={styles.big}>{usd(overview.spend?.month_usd)}</div>
        </div>
        <div className={styles.card}>
          <h3>predictions</h3>
          {Object.entries(overview.predictions || {}).map(([k, n]) => (
            <div key={k} className={styles.kv}>
              <span>{k}</span>
              <b>{n}</b>
            </div>
          ))}
        </div>
        <div className={styles.card}>
          <h3>queue (open)</h3>
          {Object.entries(overview.queue_open || {}).map(([k, n]) => (
            <div key={k} className={styles.kv}>
              <span>{k}</span>
              <b>{n}</b>
            </div>
          ))}
        </div>
        <div className={styles.card}>
          <h3>pool (new)</h3>
          {Object.entries(overview.pool || {}).map(([k, n]) => (
            <div key={k} className={styles.kv}>
              <span>{k}</span>
              <b>{n}</b>
            </div>
          ))}
        </div>
        <div className={styles.card}>
          <h3>spot checks</h3>
          <div className={styles.kv}>
            <span>human = Opus</span>
            <b>
              {spot.agreed ?? 0} / {spot.checked ?? 0}
            </b>
          </div>
          {(overview.opus_vs_human || []).map((r) => (
            <div key={r.source} className={styles.kv}>
              <span>Opus right ({r.source})</span>
              <b>
                {r.opus_right} / {r.n}
              </b>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3>{t("teach_ai.admin.golden")}</h3>
        <Table
          size="small"
          rowKey={(r) => `${r.source}-${r.language}`}
          pagination={false}
          dataSource={overview.golden}
          columns={[
            { title: "source", dataIndex: "source", key: "source" },
            { title: "language", dataIndex: "language", key: "language" },
            { title: "total", dataIndex: "total", key: "total" },
            { title: "verified", dataIndex: "verified", key: "verified" },
          ]}
        />
      </section>

      <section>
        <h3>{t("teach_ai.admin.versions")}</h3>
        <Table
          size="small"
          rowKey="version"
          columns={versionColumns}
          dataSource={overview.versions}
          pagination={false}
          scroll={{ x: true }}
          expandable={{
            expandedRowRender: (v) => (
              <div className={styles.versionDetail}>
                <div>
                  <h4>new</h4>
                  <ClassTable block={v.scores?.new} />
                </div>
                {v.scores?.current && (
                  <div>
                    <h4>current</h4>
                    <ClassTable block={v.scores.current} />
                  </div>
                )}
                {(v.gate?.reasons || []).length > 0 && (
                  <ul className={styles.reasons}>
                    {v.gate.reasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                )}
                {(v.gate?.thin || []).length > 0 && <p className={styles.note}>thin: {v.gate.thin.join(", ")}</p>}
              </div>
            ),
          }}
        />
      </section>

      <section>
        <h3>{t("teach_ai.admin.runs")}</h3>
        <Table
          size="small"
          rowKey="id"
          pagination={{ pageSize: 10 }}
          dataSource={overview.spend?.runs}
          columns={[
            { title: "run", dataIndex: "id", key: "id" },
            { title: "date", key: "date", render: (r) => r.created_at?.slice(0, 16).replace("T", " ") },
            { title: "pairs", dataIndex: "pairs", key: "pairs" },
            { title: "estimate", key: "est", render: (r) => usd(r.estimate_usd) },
            { title: "cost", key: "cost", render: (r) => usd(r.cost_usd) },
            { title: "status", dataIndex: "status", key: "status" },
          ]}
        />
      </section>
    </div>
  );
};

export default AdminView;
